import type { ApprovalQueueProvider } from '@tyndo/types';

/**
 * ApprovalProviderRegistry — the seam behind the shell's cross-module
 * "My Approvals" surface (UI-3).
 *
 * Tyndo has ~14 per-module approval queues (Sales discount/contract approvals,
 * Compliance change-control review, HR leave requests, Quality NCR
 * dispositions, …), each reachable only from inside its owning module. Rather
 * than re-plumb every backend, each module registers a small read-only
 * {@link ApprovalQueueProvider} here that adapts the queue it ALREADY fetches
 * into the normalised `ApprovalQueueItem` shape. The "My Approvals" shell view
 * iterates the registered providers and merges their items into one prioritized,
 * module-groupable queue; every row deep-links back to the module's native
 * surface for the actual approve/reject.
 *
 * Sits alongside the ViewRegistry / ActionRegistry / MenuRegistry family. Like
 * ViewRegistry it exposes a revision + subscribe() so the aggregating view
 * re-renders when a module registers its provider during the federated Pass 2
 * (a provider registered after the view first mounted must still light up).
 *
 * Keyed by `moduleId`, so a module registering twice (Pass 1 stub → Pass 2
 * federated re-register) replaces rather than duplicates.
 */
class ApprovalProviderRegistry {
  private providers = new Map<string, ApprovalQueueProvider>();
  private revision = 0;
  private listeners = new Set<() => void>();

  private bump(): void {
    this.revision += 1;
    const subs = Array.from(this.listeners);
    for (const fn of subs) {
      try {
        fn();
      } catch (err) {
        console.error('ApprovalProviderRegistry listener threw', err);
      }
    }
  }

  getRevision(): number {
    return this.revision;
  }

  /** Subscribe to registry changes. Returns an unsubscribe function. */
  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Register (or replace) a module's approval provider. Keyed by
   * `provider.moduleId`, so a re-registration from the same module overwrites
   * its earlier entry instead of adding a duplicate.
   */
  register(provider: ApprovalQueueProvider): void {
    if (!provider.moduleId) {
      console.warn('ApprovalProviderRegistry.register: provider is missing moduleId — ignored.');
      return;
    }
    this.providers.set(provider.moduleId, provider);
    this.bump();
  }

  /** Remove a module's provider (e.g. on tenant switch / module unload). */
  unregister(moduleId: string): void {
    if (this.providers.delete(moduleId)) {
      this.bump();
    }
  }

  get(moduleId: string): ApprovalQueueProvider | undefined {
    return this.providers.get(moduleId);
  }

  /** All registered providers, in registration order. */
  list(): ApprovalQueueProvider[] {
    return Array.from(this.providers.values());
  }

  has(moduleId: string): boolean {
    return this.providers.has(moduleId);
  }

  clear(): void {
    this.providers.clear();
    this.bump();
  }
}

export const approvalProviderRegistry = new ApprovalProviderRegistry();
